'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { Button } from '../ui/button';

import { CgArrowLeft, CgArrowRight } from 'react-icons/cg';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';

interface EventsGalleryProps {
  title: string;
  gallery: TDynamicData[];
}

export function EventsGallery({ title, gallery }: EventsGalleryProps) {
  const { t } = useTranslations();

  return (
    <CarouselProvider>
      <section className={cn('custom-section', 'py-10 pr-0 sm:py-12 lg:py-14 lg:pr-6 xl:py-16')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 md:flex-row md:items-center md:gap-8 lg:gap-16',
          )}
        >
          <div className="flex flex-col items-start justify-start gap-12 md:max-w-[295px]">
            <h4 className="w-full font-heading text-5xl font-medium text-black sm:text-6xl lg:text-7xl">
              {title}
            </h4>
            <Button asChild variant={'dark'} className="hidden md:flex">
              <Link prefetch={false} prefetchOnHover href={'/galeria'}>
                {t('text1943')}
              </Link>
            </Button>
          </div>
          <div className="relative w-full max-w-[880px]">
            <Carousel
              className={{
                container: 'w-full gap-4',
                slide:
                  'max-w-[200px] sm:max-w-[240px] md:max-w-[340px] lg:max-w-[360px] xl:max-w-[400px]',
                wrapper: 'w-full',
                main: 'w-full',
              }}
              slides={gallery.map(event => (
                <GallerySlide event={event} key={event.slug} />
              ))}
            />
            <div className="absolute bottom-6 right-6 z-20 hidden md:block">
              <CarouselControls
                nextIcon={<CgArrowRight size={24} className="text-white" />}
                prevIcon={<CgArrowLeft size={24} className="text-white" />}
                className={{
                  main: 'hidden items-center justify-start gap-4 md:flex',
                  button:
                    'min-h-[52px] min-w-[52px] border-none bg-primary text-white shadow-none hover:bg-primary/90',
                }}
              />
            </div>
          </div>

          <div className="flex w-full justify-center pr-6 md:hidden">
            <Button asChild variant={'dark'} className="w-full">
              <Link prefetch={false} prefetchOnHover href={'/galeria'}>
                {t('text1943')}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </CarouselProvider>
  );
}

export function GallerySlide({ event, className }: { event: TDynamicData; className?: string }) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const { t } = useTranslations();

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        key={event.name}
        className={cn(
          'group cursor-pointer transition-all',
          'relative h-[315px] w-full overflow-hidden sm:h-[400px] md:h-[440px] lg:h-[550px] xl:h-[630px]',
          className,
        )}
      >
        <div
          className={cn(
            'absolute inset-0 z-[5] flex translate-y-full flex-col items-center justify-between bg-black/50 p-4 backdrop-blur-md transition-all sm:p-6 lg:p-8',
            'group-hover:translate-y-0',
          )}
        >
          <p className="w-full text-center text-sm font-normal text-white lg:text-base">
            {event.fields[1]?.value}
          </p>
          <div className="flex w-full flex-col items-center justify-center gap-12">
            <div className="flex w-full flex-col items-center justify-center gap-2 lg:gap-2.5">
              <h4
                className={cn(
                  'w-full text-center text-2xl font-medium text-white md:text-3xl',
                  'font-heading tracking-tight',
                )}
              >
                {event.name}
              </h4>
              <p className="w-full text-center text-sm font-normal text-white lg:text-base">
                {event.fields[0]?.value}
              </p>
            </div>
            {!!event.fields[3]?.value && (
              <p className="w-full text-center text-sm font-normal text-white/80">
                {event.fields[3]?.value}
              </p>
            )}
          </div>

          <span
            onClick={handleOpenGallery(event.fields?.[2]?.images ?? [], 0)}
            className="w-full text-center text-sm font-bold text-white underline lg:text-base"
          >
            {t('text1944')}
          </span>
        </div>
        <Image
          src={event.fields?.[2]?.images?.[0]?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(event?.fields?.[2]?.images?.[0], event?.name)}
          height={0}
          width={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="(max-width: 640px) 250px, (max-width: 768px) 350px, 400px"
        />
      </div>
    </>
  );
}
